// routes.js
import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import useLocalStorage from "react-use-localstorage/dist";
import WishToaster from "./components/WishToaster";

import Dashboard from "./views/Dashboard";
import SignIn from "./views/authentication/signin";
import Sales from "./views/sales/Sales";
import Enrollment from "./views/enrollment/Enrollment";
import MyGeneology from "./views/Genealogy/MyGeneology";
import Announcements from "./views/Announcements";
import Analysis from "./views/analysis/Analysis";
import VmailNew from "./views/vmail/Vmail";
import ReportsDash from "./views/Reports/ReportsDash";
import Events from "./views/Events/EventsDashboard";
import Markettingcollateral from "./views/MarkettingCollateral/MarkettingcollateralDashboard";
import CreditNote from "./views/CreditNote";
import { EnrollmentProvider } from "./contexts/EnrollUserContext";
import PaymentSuccess from "./views/sales/marketingordercomponents/PaymentSuccess";
import UnauthenticatedPaymentSuccess from "./views/sales/marketingordercomponents/UnauthenticatedPaymentSuccess";
import PaymentFailure from "./views/sales/marketingordercomponents/PaymentFailure";
import VVInvoice from "./views/VVInvoice";
import RankProgressionTimeline from "./views/RankProgressionTimeline";
import MyCoreTeam from "./views/MyCoreTeam/MyCoreTeam";

const StorelinkOrders = React.lazy(() => import("./views/storelinkorders/StorelinkOrders"));
const Error404 = React.lazy(() => import("./views/Error404"));
const ReportGeneral = React.lazy(() => import("./views/Reports/ReportGeneral"));
const VolumeReport = React.lazy(() => import("./views/Reports/VolumeReport"));
//const InactiveIdWithoutFirstOrderReport = React.lazy(() => import("./views/Reports/InactiveIdWoFoReport"));
const ReportGeneralComponent = React.lazy(() => import("./views/Reports/ReportGeneralComponent"));
const VotmInventory = React.lazy(() => import("./views/sales/votminventory/VotmInventory"));
const VOTMDetailsPage = React.lazy(() => import("./views/sales/votminventory/VotmDetailsPage"));
const VOTMConsolidatedOrderDetails = React.lazy(() => import("./views/sales/votminventory/VotmConsolidatedDetails"));
const PlaceOrder = React.lazy(() => import("./views/sales/PlaceOrder"));
const CongratulationsPage = React.lazy(() => import("./views/CongratulationsPage"));
const FailurePage = React.lazy(() => import("./views/FailurePage"));
const CommissionReports = React.lazy(() => import("./views/Reports/CommissionReports"));
const DeductionsAndAdjustments = React.lazy(() => import("./views/Reports/DeductionsAndAdjustments"));
const CoApplicantBonusReport = React.lazy(() => import("./views/Reports/CoApplicantBonusReport"));
const NewDistributorReports = React.lazy(() => import("./views/Reports/DistributorReports"));
const SelectFromGeneology = React.lazy(() => import("./views/Genealogy/SelectFromGenealogy"));
const ProfileDashboard = React.lazy(() => import("./views/personal/ProfileDashboard"));
const MyOrders = React.lazy(() => import("./views/sales/MyOrders"));
const EnrollUser = React.lazy(() => import("./views/enrollment/EnrollUser"));
const OngoingEnrollments = React.lazy(() => import("./views/enrollment/OngoingEnrollments"));
const GenerateLink = React.lazy(() => import("./views/enrollment/GenerateLink"));
const OrderDetails = React.lazy(() => import("./views/sales/OrderDetails"));
const GenealogySettings = React.lazy(() => import("./views/Genealogy/GenealogySettings"));
const Notifications = React.lazy(() => import("./views/Notifications"));
const Settings = React.lazy(() => import("./views/personal/Settings"));
const TrendPlotGeneral = React.lazy(() => import("./views/Reports/TrendPlotGeneral"));
const Invoice = React.lazy(() => import("./views/Invoice"));
const VOTMInvoice = React.lazy(() => import("./views/VOTMInvoice"));
const CustomerLeadReport = React.lazy(() => import("./views/Reports/CustomerReorderLeads"));
const AvailRubyRewards = React.lazy(() => import("./views/sales/AvailRubyRewards"));
const MobileAppReport = React.lazy(() => import("./views/Reports/MobileAppReport"));
const BonusReport = React.lazy(() => import("./views/Reports/BonusReport"));
const NewEnrollmentReport = React.lazy(() => import("./views/enrollment/Newenrollment"));
const EnterPcmretailerDetails = React.lazy(() => import("./views/sales/EnterPcmretailerdetails"));
const IDCard = React.lazy(() => import("./components/IDCard"));
const WelcomeCard = React.lazy(() => import("./components/WelcomeCard"));
const InsuranceCard = React.lazy(() => import("./components/InsuranceCard"));
const CoApplicantInsuranceCard = React.lazy(() => import("./components/CoApplicantInsuranceCard"));
const TaxInvoiceWebview = React.lazy(() => import("./views/sales/TaxInvoiceWebview"));
const PersonalEnrollmentTree = React.lazy(() => import("./views/Genealogy/PersonalEnrollmentTree"));
const NewGenealogy = React.lazy(() => import("./views/Genealogy/NewGenealogy"));
const PlaceMarketingOrder = React.lazy(() => import("./views/sales/PlaceMarketingOrder"));
const EventSales = React.lazy(() => import("./views/sales/EventSales"));
const EventsList = React.lazy(() => import("./views/Events/Eventlist"));
const EventRegistration = React.lazy(() => import("./views/Events/EventRegistration"));
const EventRegistrationList = React.lazy(() => import("./views/Reports/EventRegisteredList"));
const TopPerformerReport = React.lazy(() => import("./views/Reports/TopPerformersReport"));
const PCMMembershipManage = React.lazy(() => import("./views/personal/PCMMembershipManage"));

const RoutesComponent = () => {
  const [isSignedIn, setIsSignedIn] = useLocalStorage("isSignedIn", false);

  const Protected = ({ isSignedIn, children }) => {
    if (!isSignedIn) {
      WishToaster({
        toastMessage: "You need to login.",
        toastType: "error",
      });
      return <Navigate to="/signin" replace />;
    }
    return children;
  };

  const AuthRoute = ({ isSignedIn, children, redirectPath = "/" }) => {
    return isSignedIn ? <Navigate to={redirectPath} replace /> : children;
  };

  const loadingUI = (
    <div className="lds-roller">
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  );

  const routesData = [
    { path: "/placeorder", component: <PlaceOrder /> },
    { path: "/placeordermarketingcollaterals", component: <PlaceMarketingOrder /> },
    { path: "/myorders", component: <MyOrders /> },
    { path: "/orderdetails", component: <OrderDetails /> },
    {
      path: "/enrolluser",
      component: (
        <EnrollmentProvider>
          <EnrollUser />
        </EnrollmentProvider>
      ),
    },
    { path: "/ongoingenrollments", component: <OngoingEnrollments /> },
    { path: "/generatelink", component: <GenerateLink /> },
    { path: "/selectfromgenealogy", component: <SelectFromGeneology isWebview={false} /> },
    { path: "/mygeneologysettings", component: <GenealogySettings /> },
    { path: "/deductionsandadjustments", component: <DeductionsAndAdjustments /> },
    { path: "/commissionreports", component: <CommissionReports /> },
    { path: "/enrollmentreport", component: <NewDistributorReports /> },
    { path: "/co-applicantbonusreport", component: <CoApplicantBonusReport /> },
    { path: "/votmorderreports", component: <ReportGeneral reportID={4} title={"VOTM Order Report"} /> },
    { path: "/storelinkorderreports", component: <ReportGeneral reportID={5} title={"Storelink Order Report"} /> },
    { path: "/orderreports", component: <ReportGeneral reportID={6} title={"Order Report"} /> },
    { path: "/myvoucherreport", component: <ReportGeneral reportID={1} title={"My Voucher Report"} /> },
    { path: "/volumereport", component: <VolumeReport /> },
    { path: "/teamreport", component: <ReportGeneral reportID={3} title={"Team report"} /> },
    {
      path: "/malaysiadownlineachieversreport",
      component: <ReportGeneral reportID={8} title={"Malaysia Downline Achievers"} />,
    },
    //  { path: "/topperformersreport", component: <ReportGeneralComponent reportID={8} title={"TopPerformers report"} /> },
    { path: "/bonusreport", component: <BonusReport /> },
    { path: "/eventregisteredlist", component: <EventRegistrationList /> },
    { path: "/eventlist", component: <EventsList /> },
    { path: "/eventsregisteration", component: <EventRegistration /> },
    { path: "/topperformersreport", component: <TopPerformerReport /> },
    { path: "/pcm-membership-manage", component: <PCMMembershipManage /> },

    {
      path: "/sponsoreddistributorreport",
      component: <ReportGeneralComponent reportID={4} title={"Sponsored Direct Seller report"} />,
    },
    {
      path: "/personalenrollmentreport",
      component: <ReportGeneralComponent reportID={7} title={"Personal Enrollment report"} />,
    },
    {
      path: "/storeorderpendingreport",
      component: <ReportGeneralComponent reportID={10} title={"Storelink Pending Order Report"} />,
    },
    {
      path: "/digirockstarreport",
      component: <ReportGeneralComponent reportID={11} title={"DigiRockstar Achieved Report"} />,
    },
    {
      path: "/digirockstarmonthlyreport",
      component: <ReportGeneralComponent reportID={12} title={"DigiRockstar Achieved Monthly Report"} />,
    },
    {
      path: "/productivedistributorreport",
      component: <ReportGeneralComponent reportID={13} title={"Productive Direct Seller Report"} />,
    },
    {
      path: "/consistentdistributorreport",
      component: <ReportGeneralComponent reportID={14} title={"Consistent Direct Seller Report"} />,
    },
    {
      path: "/activedistributorreport",
      component: <ReportGeneralComponent reportID={15} title={"Active Direct Seller Report"} />,
    },
    {
      path: "/downlinepresidentsclubreport",
      component: <ReportGeneralComponent reportID={18} title={"Downline President's Club"} />,
    },
    { path: "/firstordertrend", component: <TrendPlotGeneral /> },
    { path: "/reordertrend", component: <TrendPlotGeneral /> },
    { path: "/ranktrend", component: <TrendPlotGeneral /> },
    { path: "/volumetrend", component: <TrendPlotGeneral /> },
    { path: "/myprofile", component: <ProfileDashboard /> },
    { path: "/settings", component: <Settings /> },
    { path: "/paymentsuccess", component: <CongratulationsPage /> },
    { path: "/paymentfailed", component: <FailurePage /> },
    { path: "/notifications", component: <Notifications /> },
    { path: "/votminventory", component: <VotmInventory /> },
    { path: "/mydeliverydetails", component: <VOTMDetailsPage /> },
    {
      path: "/votmconsolidatedorderdetails",
      component: <VOTMConsolidatedOrderDetails />,
    },
    { path: "/downloadorderdetails", component: <Invoice /> },
    { path: "/downloadvvorderdetails", component: <VVInvoice /> },
    { path: "/downloadvotminvoices", component: <VOTMInvoice /> },
    { path: "/downloadcreditnote", component: <CreditNote /> },
    { path: "/customerreorderleads", component: <CustomerLeadReport /> },
    { path: "/redeemrubyrewards", component: <AvailRubyRewards /> },
    { path: "/enterpcmretailerdetails", component: <EnterPcmretailerDetails /> },
    { path: "/mobileappteamcountreport", component: <MobileAppReport /> },
    { path: "/distributoridcard", component: <IDCard /> },
    { path: "/distributorwelcomecard", component: <WelcomeCard /> },
    { path: "/distributorinsurancecard", component: <InsuranceCard /> },
    { path: "/coapplicantinsurancecard", component: <CoApplicantInsuranceCard /> },
    { path: "/newenrollmentreport", component: <NewEnrollmentReport /> },
    //  {  path: "/inactiveidswoforeport", component: <InactiveIdWithoutFirstOrderReport reportID={16} title={"Inactive IDs without First Order Report"} />, },
    { path: "/personalenrollmenttree", component: <PersonalEnrollmentTree /> },
    {
      path: "/newgenealogy",
      component: (
        <NewGenealogy onEnrollNodeClick={(node) => alert(`Enroll a new node under parent ID: ${node.parentDistId}`)} />
      ),
    },
    { path: "/enterpcmretailerdetails", component: <EnterPcmretailerDetails /> },
  ];

  const webviews = [
    {
      path: "/taxinvoicewebview",
      component: <TaxInvoiceWebview />,
    },
    {
      path: "/selectfromgenealogy_webview",
      component: <SelectFromGeneology isWebview={true} />,
    },
    {
      path: "/deductionsandadjustments_webview",
      component: <DeductionsAndAdjustments isWebview={true} />,
    },
    {
      path: "/commissionreports_webview",
      component: <CommissionReports isWebview={true} />,
    },
    {
      path: "/enrollmentreport_webview",
      component: <NewDistributorReports isWebview={true} />,
    },
    {
      path: "/co-applicantbonusreport_webview",
      component: <CoApplicantBonusReport isWebview={true} />,
    },
    {
      path: "/volumereport_webview",
      component: <VolumeReport isWebview={true} />,
    },
    {
      path: "/orderreports_webview",
      component: <ReportGeneral reportID={6} title={"Order Report"} isWebview={true} />,
    },
    {
      path: "/votmorderreports_webview",
      component: <ReportGeneral reportID={4} title={"VOTM Order Report"} isWebview={true} />,
    },
    {
      path: "/storelinkorderreports_webview",
      component: <ReportGeneral reportID={5} title={"Storelink Order Report"} isWebview={true} />,
    },
    {
      path: "/myvoucherreport_webview",
      component: <ReportGeneral reportID={1} title={"My Voucher Report"} isWebview={true} />,
    },
    {
      path: "/teamreport_webview",
      component: <ReportGeneral reportID={3} title={"Team Report"} isWebview={true} />,
    },
    {
      path: "/malaysiadownlineachieversreport_webview",
      component: <ReportGeneral reportID={8} title={"Malaysia Downline Achievers"} isWebview={true} />,
    },
    {
      path: "/bonusreport_webview",
      component: <ReportGeneralComponent reportID={3} title={"Bonus Report"} isWebview={true} />,
    },
    {
      path: "/sponsoreddistributor_webview",
      component: <ReportGeneralComponent reportID={4} title={"Sponsored Direct Seller Report"} isWebview={true} />,
    },
    {
      path: "/topperformersreport_webview",
      component: <ReportGeneralComponent reportID={8} title={"TopPerformers Report"} isWebview={true} />,
    },
    {
      path: "/storeorderpendingreport_webview",
      component: <ReportGeneralComponent reportID={10} title={"Store Orders Pending Report"} isWebview={true} />,
    },

    {
      path: "/customerreorderleads_webview",
      component: <CustomerLeadReport reportID={11} title={"Customer Re-order leads"} isWebview={true} />,
    },
    {
      path: "/mobileappteamcountreport_webview",
      component: <MobileAppReport reportID={12} title={"Direct Sellers Yet to Download"} isWebview={true} />,
    },
    {
      path: "/storeorderpendingreport_webview",
      component: <ReportGeneralComponent reportID={13} title={"Storelink Pending Order Report"} isWebview={true} />,
    },
  ];

  return (
    <Suspense fallback={loadingUI}>
      <Routes>
        <Route path="/*" element={<Error404 />} />
        <Route
          path="/signin"
          element={
            <AuthRoute isSignedIn={isSignedIn}>
              <SignIn setisAutheticated={setIsSignedIn} />
            </AuthRoute>
          }
        />
        {/* Event Sales Route - Unauthenticated */}
        <Route
          path="/event_sale"
          element={<EventSales />}
        />
        {/* Payment Success/Failure Routes - Unauthenticated */}
        <Route
          path="/vvpaymentsuccess"
          element={<UnauthenticatedPaymentSuccess />}
        />
        <Route
          path="/vvpaymentfailure"
          element={<PaymentFailure />}
        />
        {/* <Route path="*" element={<Navigate to={isSignedIn ? "/" : "/signin"} replace />} /> */}

        <Route
          path="/"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Dashboard />
            </Protected>
          }
        />
        <Route
          path="/sales"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Sales />
            </Protected>
          }
        />
        <Route
          path="/storelinkorders"
          element={
            <Protected isSignedIn={isSignedIn}>
              <StorelinkOrders />
            </Protected>
          }
        />
        <Route
          path="/enrollment"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Enrollment />
            </Protected>
          }
        />
        <Route
          path="/mygeneology"
          element={
            <Protected isSignedIn={isSignedIn}>
              <MyGeneology isWebview={false} />
            </Protected>
          }
        />
        <Route path="/mygeneology_webview" element={<MyGeneology isWebview={true} />} />
        <Route
          path="/analysis"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Analysis />
            </Protected>
          }
        />
        <Route
          path="/viewanalysisdashboardreadonly"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Analysis readonly={true} />
            </Protected>
          }
        />
        <Route
          path="/reportsandtrends"
          element={
            <Protected isSignedIn={isSignedIn}>
              <ReportsDash />
            </Protected>
          }
        />
        <Route
          path="/events"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Events />
            </Protected>
          }
        />
        <Route
          path="/markettingcollateral"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Markettingcollateral />
            </Protected>
          }
        />
        <Route
          path="/announcements"
          element={
            <Protected isSignedIn={isSignedIn}>
              <Announcements />
            </Protected>
          }
        />
        <Route
          path="/vmail"
          element={
            <Protected isSignedIn={isSignedIn}>
              <VmailNew />
            </Protected>
          }
        />
        <Route
          path="/rank-journey-dev"
          element={
            <Protected isSignedIn={isSignedIn}>
              <RankProgressionTimeline />
            </Protected>
          }
        />
        <Route
          path="/mycoreteam"
          element={
            <Protected isSignedIn={isSignedIn}>
              <MyCoreTeam />
            </Protected>
          }
        />
        {webviews.map((item, index) => (
          <Route key={index} path={item.path} element={item.component} />
        ))}
        {routesData.map((item, index) => (
          <Route
            key={index}
            path={item.path}
            element={<Protected isSignedIn={isSignedIn}>{item.component}</Protected>}
          />
        ))}
      </Routes>
    </Suspense>
  );
};

export default RoutesComponent;
